import * as THREE from 'three'
import ReactDOM from 'react-dom'
import { useRef } from 'react'
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { SimpleReverb } from './SimpleReverb'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import './styles.css'

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
let bg = "white"

let colDec = randI(200) * 0.01
let peq = 0.01
let melodyLen = 16
let NUM = 12;

var getFrequency = function (_) { var e, r; return e = 3 === _.length ? _.charAt(2) : _.charAt(1), 440 * Math.pow(2, ((r = (r = ["A", "A#", "B", "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#"].indexOf(_.slice(0, -1))) < 3 ? r + 12 + (e - 1) * 12 + 1 : r + (e - 1) * 12 + 1) - 49) / 12) };

let getBpm = () => (12 + randI(20)) * 5
let getPeq = () => peq + (randI(40) * 0.0005 * rand([1, 1.1, 1.25, 1.3, 1.5]) * rand([1, -1]))
let getDur = () => 1 + randI(4)
let getCutoff = () => (50 + (randI(10) * 0.1)) * 5
let getFreq = () => getFrequency(rand(['C', 'D', 'E', 'F', 'G']) + rand([2, 3, 4, 5, 6]))

let peqs = new Array(melodyLen).fill().map(() => getPeq())
let gate = new Array(melodyLen).fill().map(() => randI(20) > 13)
let durs = new Array(melodyLen).fill().map(() => getDur())
let pan = new Array(melodyLen).fill().map(() => randI(20) > 8 ? (randI(20) * 0.1) - 1 : 0)
let cutoff = new Array(melodyLen).fill().map(() => getCutoff())
let reverbTime = new Array(melodyLen).fill().map(() => 4 + randI(10))
let sqrrrt = 9 + (randI(90) * 0.05)
let detail = 10 + randI(rand([10, 10, 10, 20, 20, 30]))
let colMult = 1 + (randI(30) * 0.1) + (randI(100) * 0.001)
let colPush = 0.1 + (randI(80) * 0.01)
let toType = rand([0, 0, 0, 0, 0, 0, 0, 0, 1])
let bitCheck = rand([2, 2, 2, 3, 4, 1 + randI(30)])
let smoothing = 1 + (randI(500) * 0.01)
let startTime = 1 + randI(6)
let tri = rand(['sin', 'cos'])
let triSize = 1 + randI(15)
let triSize2 = 1 + randI(10)
let colSpread = (1+randI(8))*0.05

class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.uniforms.time = { value: startTime }
    this.shaderMaterial.uniforms.colMult = { value: colMult }
    this.shaderMaterial.uniforms.colDec = { value: colDec }
    this.shaderMaterial.uniforms.peq = { value: peq }
    this.shaderMaterial.uniforms.sqrrrt = { value: sqrrrt }
    this.shaderMaterial.uniforms.detail = { value: detail }
    this.shaderMaterial.uniforms.toType = { value: toType }
    this.shaderMaterial.uniforms.bitCheck = { value: bitCheck }
    this.shaderMaterial.uniforms.smoothing = { value: smoothing }
    this.shaderMaterial.uniforms.colPush = { value: colPush }
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
uniform float time;
uniform float colDec;
uniform float peq;
uniform float sqrrrt;
uniform float colMult;
uniform float smoothing;
uniform int detail;
uniform float colPush;
uniform int toType;
uniform uint bitCheck;
varying vec2 vUv;

uint ihash(uint x) {
  x += 0x12345678u;
    x ^= x >> 16;
    x *= 0x7feb352dU;
    x ^= x >> 15;
    x *= 0x846ca68bU;
    x ^= x >> 12;
    return x;
}

vec3 h2rgb(float h) {
  vec3 rgb = clamp( abs(mod(h*22.0+vec3(0.0,4.0,3.0),colMult)-2.0)-1.0, 0.0, 1.0 );
  return rgb*rgb*(4.0-smoothing*rgb); // cubic smoothing	
}

float rectdist(vec2 p, vec2 a, vec2 b) {
  return max(max(a.y-p.y,p.y-b.y),
             max(a.x-p.x,p.x-b.x));
}
vec4 when_gt( vec4 x, float y ) {
    return max( sign( x - y ), 0.000000001 );
}

void main() {
  float phi = peq*(sqrt(sqrrrt)-0.5); 
  vec2 p = (-1.0 + 0.5 *vUv);
  float px = fwidth(p.x);
  vec3 col = vec3(0,0,1);
  vec2 a = vec2(-1.0-phi,-1);
  vec2 b = vec2(1.0+phi,1);
  int type = 0;
  uint h = uint(time*0.0);
  float d = rectdist(p,a,b);
  if (d < 0.0) {
    int N = detail;
    for (int i = 0; 
         i < N; 
         i++, h = ihash(h^uint(type))) {
      if (type == 0) {
        bool flip = bool(h & bitCheck);
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
          float x1 = (1.0-phi)*a.x + phi*b.x*(${tri}(time*0.5*p.y-p.x*${triSize2}.0)*p.x*${triSize}.0);
          if (p.x < x1) { 
            b = vec2(x1,b.x); 
            type = 1; 
          }
          else { 
            a = vec2(x1,a.y); 
            type = 2; 
          }
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
      } else {
        bool flip = b.y-a.y > b.x-a.x;
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
          float x1 = a.x+b.y-a.y;
          if (p.x < x1) { 
            b = vec2(x1,b.y); 
            type = 0; 
          }
          else { 
            a = vec2(x1,a.y); 
            type = toType; 
          }
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
      }
    }
    col = h2rgb(colDec+colPush+(float(h)*${colSpread})/exp2(32.0));
  }
  gl_FragColor = vec4(col,0.01);
}

`
    }
  }
}
let timeSpeed = 0.2+ (randI(22)*0.1)
extend({ EffectComposer, RenderPass, AfterBurn })
let pi = 0
let pt = peqs[0]
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.peq.value = THREE.MathUtils.lerp(ref.current.uniforms.peq.value, pt, 1)
    if(started){
      ref.current.uniforms.time.value += 0.002 * (wild ? timeSpeed : 0.1)
    }
    reverb.cutoff.value = cutoff[pi] - Math.sin(ref.current.uniforms.time.value * 2) * 120
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}
let wild = false
let started = false
function App() {
  const start = () => {
    play()
    if (!audioStarted) {
      whiteNoise.start(0);
      audioStarted = true;
      started = true
    }
    wild = !wild
    if (!wild) {
      fgains.forEach((g) => {
        g.gain.linearRampToValueAtTime(1, audioContext.currentTime + 0.02)
      })
    }
  }
  return (
    <div id="main" onClick={() => start()}
      onDoubleClick={() => {
        toggleFullScreen()
      }}
    >
      <Canvas flat={false} linear={false} dpr={[1, 2]} camera={{ position: [0, 0, 2], near: 0.0001, far: 1, fov: 35 }}
        gl={{ alpha: false, antialias: false, stencil: false, depth: false }}
      >
        <color attach="background" args={[bg]} />
        <OrbitControls />
        <Tint />
      </Canvas>
    </div>
  )
}

ReactDOM.render(
  <App />,
  document.getElementById('root')
)

window.AudioContext = window.AudioContext || window.webkitAudioContext;
let audioStarted = false;
let audioContext = new AudioContext();
let bufferSize = 2 * audioContext.sampleRate,
  noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate),
  output = noiseBuffer.getChannelData(0);
let b0, b1, b2, b3, b4, b5, b6;
b0 = b1 = b2 = b3 = b4 = b5 = b6 = 0.0;
for (let i = 0; i < bufferSize; i++) {
  const white = fxrand() * 2 - 1;
  b0 = 0.99886 * b0 + white * 0.0555179;
  b1 = 0.99332 * b1 + white * 0.0750759;
  b2 = 0.96900 * b2 + white * 0.1538520;
  b3 = 0.86650 * b3 + white * 0.3104856;
  b4 = 0.55000 * b4 + white * 0.5329522;
  b5 = -0.7616 * b5 - white * 0.0168980;
  output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
  output[i] *= 0.13; // (roughly) compensate for gain
  b6 = white * 0.115926;
}

let whiteNoise = audioContext.createBufferSource();
whiteNoise.buffer = noiseBuffer;
whiteNoise.loop = true;

let reverb = SimpleReverb(audioContext);
reverb.connect(audioContext.destination);
reverb.time = 7
reverb.filterType = 'lowpass'
reverb.cutoff.value = 900
reverb.wet.value = 0.1
reverb.dry.value = 0.075

let filters = [];
let fgains = [];
let panners = [];
let freqs = []

for (let i = 0; i < NUM; i++) {
  let f = audioContext.createBiquadFilter();
  let p = audioContext.createStereoPanner();
  panners.push(p)
  f.type = "bandpass";
  f.Q.value = 300;
  let fr = getFreq()
  f.frequency.value = fr * rand([1, 2, 3, 4])
  freqs.push(fr)
  filters.push(f);
  let g = audioContext.createGain();
  g.gain.value = 0;
  fgains.push(g);
}

for (let i = 0; i < NUM; i++) {
  whiteNoise.connect(filters[i]);
  filters[i].connect(fgains[i]);
  fgains[i].connect(panners[i]);
  panners[i].connect(reverb);
}

function toggleFullScreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  }
}
let c = 0
let beat = () => {

  if (!wild) {
    return;
  }
  if (c % 64 === 0) {
    new Array(4).fill().map(() => {
      gate[randI(melodyLen)] = randI(10) > 2
    })
    if (randI(20) > 15) {
      gate = new Array(melodyLen).fill().map(() => randI(20) > 18)
    }
    if (randI(20) > 10) {
      durs[randI(melodyLen)] = getDur()
      cutoff[randI(melodyLen)] = getCutoff()
    }
    if (randI(20) > 5) {
      rand(filters).frequency.value = getFreq()
      rand(filters).frequency.value = getFreq()
    }
    bpm = randI(10) > 7 ? getBpm() : bpm
    console.log(gate, peqs, bpm)
  }
  c++
  pi = (pi + 1) % melodyLen
  if (!gate[pi]) {
    return;
  }
  panners.forEach((p, i) => {
    p.pan.value = i % 2 ? pan[pi] : -pan[pi]
  })
  reverb.cutoff.value = cutoff[pi]
  reverb.time = reverbTime[pi]

  fgains.forEach((g) => {
    g.gain.linearRampToValueAtTime(20, audioContext.currentTime + 0.01)
    g.gain.linearRampToValueAtTime(1, audioContext.currentTime + 0.02 + (durs[pi] / bpm))
  })
  console.log(pi)
  pt = THREE.MathUtils.lerp(pt, peqs[pi], 1)
}
function scheduleNote(beatNumber, time) {
  notesInQueue.push({ note: beatNumber, time: time });
  beat()
}
function scheduler() {
  while (nextNoteTime < audioContext.currentTime + scheduleAheadTime) {
    scheduleNote(current16thNote, nextNoteTime);
    nextNote();
  }
}

function nextNote() {
  var secondsPerBeat = 60.0 / bpm;
  nextNoteTime += 0.25 * secondsPerBeat;
  current16thNote++;
  if (current16thNote == 16) {
    current16thNote = 0;
  }
}

var isPlaying = false;
var current16thNote;
var bpm = getBpm() * 0.6
var lookahead = 5.0;
var scheduleAheadTime = 0.1;
var nextNoteTime = 0.0;
var notesInQueue = [];
var timerWorker = null;
function play() {
  isPlaying = true;
  if (isPlaying) {
    current16thNote = 0;
    nextNoteTime = audioContext.currentTime;
    timerWorker.postMessage("start");
    return "stop";
  } else {
    timerWorker.postMessage("stop");
    return "play";
  }
}

function init() {
  timerWorker = new Worker("./metronomeworker.js");
  timerWorker.onmessage = function (e) {
    if (e.data == "tick") {
      scheduler();
    }
    else
      console.log("message: " + e.data);
  };
  timerWorker.postMessage({ "interval": lookahead });
}
window.addEventListener("load", init);
console.log('%c 🍬🍬 @ramioso 2023-02-26 🍬🍬', 'color: hotpink');
